import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Stack, Tabs, Tab, Paper, TextField, MenuItem, IconButton, Menu,
  ListItemIcon, ListItemText, ListSubheader, Divider, Snackbar, Alert, AlertTitle, Chip, ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Collapse, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditNotificationsOutlinedIcon from '@mui/icons-material/EditNotificationsOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import {
  BoardColumn, Pagination, SalonMember, TaskCategory, TaskDetail, TaskFilters, TaskListItem,
  TaskPriority, TaskStatus, taskApi, taskErrorCode, taskErrorMessage,
} from '../../services/taskService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { useAuthStore } from '../../store/authStore';
import { useTaskTimerStore } from '../../store/taskTimerStore';
import { tokens } from '../../theme';
import { PageHeader, SectionCard, EmptyState, useConfirm } from '../../components/ui';
import { countLabel } from '../../utils/format';
import {
  PRIORITY_COLOR, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_COLOR, STATUS_LABEL, STATUS_ORDER, loadPreference, savePreference,
  userLabel,
} from './taskShared';
import TaskBoard from './TaskBoard';
import TaskListView from './TaskListView';
import TaskFormDialog from './TaskFormDialog';
import TaskNotificationSettingsDialog from './TaskNotificationSettingsDialog';

type TabKey =
  | 'mine' | 'all' | 'today' | 'upcoming' | 'overdue' | 'review' | 'approval' | 'done' | 'archive';

const OPEN_STATUSES: TaskStatus[] = [
  'TODO', 'IN_PROGRESS', 'ON_HOLD', 'IN_REVIEW', 'IN_APPROVAL', 'NEEDS_REWORK',
];

const TABS: { key: TabKey; label: string; managerOnly?: boolean }[] = [
  { key: 'mine', label: 'Moje zadania' },
  { key: 'all', label: 'Wszystkie' },
  { key: 'today', label: 'Na dziś' },
  { key: 'upcoming', label: 'Nadchodzące' },
  { key: 'overdue', label: 'Przeterminowane' },
  { key: 'review', label: 'Do weryfikacji' },
  { key: 'approval', label: 'Do akceptacji' },
  { key: 'done', label: 'Zakończone' },
  { key: 'archive', label: 'Archiwum', managerOnly: true },
];

const PREF_KEY = 'visitio_tasks_view_prefs';

interface ViewPrefs {
  view: 'board' | 'list';
  tab: TabKey;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  limit: number;
}

const DEFAULT_PREFS: ViewPrefs = {
  view: 'board',
  tab: 'mine',
  sortBy: 'dueAt',
  sortDir: 'asc',
  limit: 25,
};

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};
const startOfTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

/** Turns the active tab into concrete API filters. */
function tabFilters(tab: TabKey): TaskFilters {
  switch (tab) {
    case 'mine': return { mine: true, status: OPEN_STATUSES };
    case 'today': return { dueTo: endOfToday(), status: OPEN_STATUSES };
    case 'upcoming': return { dueFrom: startOfTomorrow(), status: OPEN_STATUSES };
    case 'overdue': return { overdue: true };
    case 'review': return { status: ['IN_REVIEW'] };
    case 'approval': return { status: ['IN_APPROVAL'] };
    case 'done': return { status: ['DONE'] };
    case 'archive': return { archived: true };
    case 'all':
    default: return {};
  }
}

/** Opis pustego widoku dla danej zakladki — mowi, dlaczego nic nie ma. */
const EMPTY_TAB_COPY: Record<TabKey, { title: string; description: string }> = {
  mine: { title: 'Nie masz otwartych zadań', description: 'Gdy ktoś przydzieli Ci zadanie albo utworzysz je dla siebie, pojawi się tutaj.' },
  all: { title: 'W salonie nie ma jeszcze zadań', description: 'Utwórz pierwsze zadanie — np. zamówienie kosmetyków albo przygotowanie gabinetu.' },
  today: { title: 'Nic na dziś', description: 'Żadne otwarte zadanie nie ma terminu na dziś.' },
  upcoming: { title: 'Brak nadchodzących zadań', description: 'Otwarte zadania z terminem od jutra pojawią się tutaj.' },
  overdue: { title: 'Wszystko w terminie', description: 'Żadne zadanie nie przekroczyło terminu.' },
  review: { title: 'Nic do weryfikacji', description: 'Zadania przekazane do weryfikacji pojawią się tutaj.' },
  approval: { title: 'Nic do akceptacji', description: 'Zadania czekające na akceptację właściciela pojawią się tutaj.' },
  done: { title: 'Brak zakończonych zadań', description: 'Zadania oznaczone jako zakończone pojawią się tutaj.' },
  archive: { title: 'Archiwum jest puste', description: 'Zarchiwizowane zadania znikają z list, ale zostają tutaj.' },
};

const TabLabel: React.FC<{ label: string; count?: number; tone: 'default' | 'danger'; selected: boolean }> = ({ label, count, tone, selected }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.875 }}>
    {label}
    {count ? (
      <Box
        component="span"
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 999,
          display: 'inline-grid',
          placeItems: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          bgcolor: tone === 'danger' ? tokens.dangerSoft : selected ? tokens.plumSoft : tokens.surfaceStrong,
          color: tone === 'danger' ? tokens.danger : selected ? tokens.plum : tokens.inkSoft,
        }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

const menuHeaderSx = { fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, lineHeight: '32px', bgcolor: 'transparent', px: 1.5 } as const;

export const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeSalonId, salons, loadSalons } = useSalonContextStore();
  const user = useAuthStore((s) => s.user);
  const refreshTimer = useTaskTimerStore((s) => s.refresh);

  const stored = loadPreference<ViewPrefs>(PREF_KEY, DEFAULT_PREFS);
  const [view, setView] = useState<'board' | 'list'>(stored.view);
  const [tab, setTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || stored.tab);
  const [sortBy, setSortBy] = useState(stored.sortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(stored.sortDir);
  const [limit, setLimit] = useState(stored.limit);
  const [page, setPage] = useState(1);

  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [priority, setPriority] = useState<TaskPriority | ''>('');
  const [assigneeId, setAssigneeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [withActiveTimer, setWithActiveTimer] = useState(false);

  const [isManager, setIsManager] = useState(false);
  const [members, setMembers] = useState<SalonMember[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [rows, setRows] = useState<TaskListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 25, total: 0, pages: 1 });
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TaskDetail | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ el: HTMLElement; task: TaskListItem } | null>(null);
  const [bulkMenu, setBulkMenu] = useState<null | HTMLElement>(null);
  const [reworkDialog, setReworkDialog] = useState<{ task: TaskListItem; reason: string } | null>(null);
  const [timerDialog, setTimerDialog] = useState<{ task: TaskListItem; status: TaskStatus } | null>(null);

  const salonId = activeSalonId || salons[0]?.id || '';

  useEffect(() => { if (salons.length === 0) loadSalons(); }, [salons.length, loadSalons]);

  // Debounced search so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    savePreference(PREF_KEY, { view, tab, sortBy, sortDir, limit });
  }, [view, tab, sortBy, sortDir, limit]);

  useEffect(() => {
    setSearchParams(tab === DEFAULT_PREFS.tab ? {} : { tab }, { replace: true });
  }, [tab, setSearchParams]);

  // Dictionaries are loaded once per salon.
  useEffect(() => {
    if (!salonId) return;
    let active = true;
    Promise.all([
      taskApi.context(),
      taskApi.members(salonId),
      taskApi.listCategories(salonId),
    ])
      .then(([ctx, mem, cats]) => {
        if (!active) return;
        setIsManager(ctx.isManager);
        setMembers(mem);
        setCategories(cats);
      })
      .catch(() => { if (active) setError('Nie udało się załadować danych salonu. Odśwież stronę, a jeśli problem wróci — spróbuj za chwilę.'); });
    return () => { active = false; };
  }, [salonId]);

  const filters: TaskFilters = useMemo(() => {
    const base = tabFilters(tab);
    return {
      salonId,
      ...base,
      ...(statusFilter ? { status: [statusFilter] } : {}),
      ...(priority ? { priority: [priority] } : {}),
      ...(assigneeId ? { assigneeId } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(search ? { search } : {}),
      ...(withActiveTimer ? { withActiveTimer: true } : {}),
    };
  }, [tab, salonId, statusFilter, priority, assigneeId, categoryId, search, withActiveTimer]);

  const reloadRef = useRef<() => void>(() => {});

  const load = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError('');
    try {
      if (view === 'board') {
        const res = await taskApi.board({ ...filters, columnLimit: 100 });
        setColumns(res.columns);
      } else {
        const res = await taskApi.list({ ...filters, sortBy, sortDir, page, limit });
        setRows(res.data);
        setPagination(res.pagination);
      }
      const c = await taskApi.counts(salonId);
      setCounts(c as unknown as Record<string, number>);
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się załadować zadań.'));
    } finally {
      setLoading(false);
    }
  }, [salonId, view, filters, sortBy, sortDir, page, limit]);

  reloadRef.current = load;
  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshTimer(); }, [refreshTimer]);
  useEffect(() => { setPage(1); setSelected([]); }, [tab, search, priority, assigneeId, categoryId, statusFilter, withActiveTimer]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const applyStatus = useCallback(async (
    task: TaskListItem,
    status: TaskStatus,
    options: { reason?: string; stopTimer?: boolean } = {},
  ) => {
    try {
      await taskApi.changeStatus(task.id, status, options);
      setToast({ msg: `Status zmieniony na „${STATUS_LABEL[status]}”.`, severity: 'success' });
      load();
      return true;
    } catch (err) {
      const code = taskErrorCode(err);
      if (code === 'ACTIVE_TIMER') {
        setTimerDialog({ task, status });
        return false;
      }
      setToast({ msg: taskErrorMessage(err, 'Nie udało się zmienić statusu.'), severity: 'error' });
      return false;
    }
  }, [load]);

  const handleMove = useCallback((task: TaskListItem, status: TaskStatus) => {
    if (status === 'NEEDS_REWORK') {
      setReworkDialog({ task, reason: '' });
      return;
    }
    applyStatus(task, status);
  }, [applyStatus]);

  const handleQuickAssign = async (task: TaskListItem, userId: string) => {
    const current = task.assignees.map((a) => a.id);
    const next = current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId];
    try {
      await taskApi.update(task.id, { assigneeIds: next });
      setToast({ msg: 'Zmieniono przypisanie.', severity: 'success' });
      load();
    } catch (err) {
      setToast({ msg: taskErrorMessage(err, 'Nie udało się zmienić przypisania.'), severity: 'error' });
    }
  };

  const openEdit = async (task: TaskListItem) => {
    try {
      const detail = await taskApi.get(task.id);
      setEditing(detail);
      setFormOpen(true);
    } catch (err) {
      setToast({ msg: taskErrorMessage(err), severity: 'error' });
    }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };

  const runBulk = async (payload: Record<string, unknown>) => {
    setBulkMenu(null);
    try {
      const res = await taskApi.bulk({ ids: selected, ...payload });
      const failedCount = res.failed.length;
      setToast({
        msg: failedCount === 0
          ? `Zaktualizowano ${countLabel(res.succeeded, 'zadanie', 'zadania', 'zadań')}.`
          : `Zaktualizowano ${res.succeeded}, pominięto ${failedCount} (brak uprawnień lub niedozwolona zmiana).`,
        severity: failedCount === 0 ? 'success' : 'info',
      });
      setSelected([]);
      load();
    } catch (err) {
      setToast({ msg: taskErrorMessage(err, 'Operacja zbiorcza nie powiodła się.'), severity: 'error' });
    }
  };

  const deleteTask = async (task: TaskListItem) => {
    const ok = await confirm({
      title: 'Usunąć zadanie?',
      details: <>„{task.title}”</>,
      description: 'Zadanie zniknie z list. Historia i zapisany czas pozostają w systemie na potrzeby audytu.',
      confirmLabel: 'Usuń zadanie',
      cancelLabel: 'Anuluj',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await taskApi.remove(task.id);
      setToast({ msg: 'Zadanie zostało usunięte.', severity: 'success' });
      load();
    } catch (err) {
      setToast({ msg: taskErrorMessage(err, 'Nie udało się usunąć zadania.'), severity: 'error' });
    }
  };

  const activeFilterCount = [statusFilter, priority, assigneeId, categoryId, search].filter(Boolean).length
    + (withActiveTimer ? 1 : 0);
  // Wyszukiwarka jest zawsze widoczna — licznik przy „Filtry” dotyczy tylko rozwijanego panelu.
  const panelFilterCount = activeFilterCount - (search ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter(''); setPriority(''); setAssigneeId(''); setCategoryId('');
    setSearchInput(''); setSearch(''); setWithActiveTimer(false);
  };

  const visibleTabs = TABS.filter((t) => !t.managerOnly || isManager);
  const currentTab = visibleTabs.some((t) => t.key === tab) ? tab : 'all';

  const tabBadge = (key: TabKey): number | undefined => {
    switch (key) {
      case 'mine': return counts.mine;
      case 'today': return counts.today;
      case 'overdue': return counts.overdue;
      case 'review': return counts.review;
      case 'approval': return counts.approval;
      default: return undefined;
    }
  };

  const headerDescription = (() => {
    const parts: string[] = [];
    if (counts.mine) parts.push(`Masz ${countLabel(counts.mine, 'otwarte zadanie', 'otwarte zadania', 'otwartych zadań')}`);
    if (counts.today) parts.push(`${counts.today} na dziś`);
    if (counts.overdue) parts.push(`${counts.overdue} po terminie`);
    return parts.length ? parts.join(' · ') : 'Planuj, przydzielaj i rozliczaj obowiązki w salonie.';
  })();

  if (!salonId) {
    return (
      <Box>
        <PageHeader title="Zadania" description="Planuj, przydzielaj i rozliczaj obowiązki w salonie." />
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Nie wybrano salonu"
            description="Wybierz salon na górnym pasku, aby zobaczyć jego zadania."
          />
        </SectionCard>
      </Box>
    );
  }

  const emptyCopy = EMPTY_TAB_COPY[currentTab];
  const loadFailed = !!error && !loading && (view === 'board' ? columns.length === 0 : rows.length === 0);
  const emptyState = (
    <SectionCard>
      {activeFilterCount > 0 ? (
        <EmptyState
          icon={<SearchOffOutlinedIcon />}
          title="Żadne zadanie nie pasuje do filtrów"
          description="Zmień wyszukiwaną frazę albo wyczyść filtry, aby zobaczyć wszystkie zadania z tej zakładki."
          actions={<Button variant="outlined" onClick={clearFilters}>Wyczyść filtry</Button>}
        />
      ) : (
        <EmptyState
          icon={<TaskAltOutlinedIcon />}
          title={emptyCopy.title}
          description={emptyCopy.description}
          actions={currentTab === 'archive' ? undefined : <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Nowe zadanie</Button>}
        />
      )}
    </SectionCard>
  );

  return (
    <Box>
      <PageHeader
        title="Zadania"
        description={headerDescription}
        actions={(
          <>
            <Button variant="outlined" startIcon={<BarChartIcon />} onClick={() => navigate('/tasks/stats')}>
              Statystyki
            </Button>
            <Tooltip title="Ustawienia powiadomień">
              <IconButton
                onClick={() => setSettingsOpen(true)}
                aria-label="Ustawienia powiadomień"
                sx={{ width: 44, height: 44, border: `1px solid ${tokens.lineStrong}`, bgcolor: tokens.paper, '&:hover': { bgcolor: tokens.plumSofter } }}
              >
                <EditNotificationsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Nowe zadanie
            </Button>
          </>
        )}
      >
        <Box sx={{ borderBottom: `1px solid ${tokens.line}`, mx: { xs: -2, sm: 0 } }}>
          <Tabs
            value={currentTab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Widoki zadań"
            sx={{ px: { xs: 1, sm: 0 }, mb: '-1px', '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' } }}
          >
            {visibleTabs.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={<TabLabel label={t.label} count={tabBadge(t.key)} tone={t.key === 'overdue' ? 'danger' : 'default'} selected={currentTab === t.key} />}
              />
            ))}
          </Tabs>
        </Box>
      </PageHeader>

      {/* Wyszukiwarka, filtry i widok */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Szukaj w tytule, opisie i komentarzach…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            inputProps={{ 'aria-label': 'Szukaj zadań' }}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, maxWidth: { sm: 440 }, minWidth: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Wyczyść wyszukiwanie" onClick={() => setSearchInput('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="task-filters"
            sx={showFilters || panelFilterCount > 0 ? { borderColor: tokens.plumLight, color: tokens.plum, bgcolor: tokens.plumSofter } : undefined}
          >
            Filtry
            {panelFilterCount > 0 && (
              <Box component="span" sx={{ ml: 0.75, minWidth: 20, height: 20, px: 0.5, borderRadius: 999, bgcolor: tokens.plum, color: tokens.paper, fontSize: '0.75rem', fontWeight: 700, display: 'inline-grid', placeItems: 'center' }}>
                {panelFilterCount}
              </Box>
            )}
          </Button>
          {activeFilterCount > 0 && !showFilters && (
            <Button size="small" variant="text" onClick={clearFilters}>Wyczyść filtry</Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            aria-label="Widok zadań"
          >
            <ToggleButton value="board" aria-label="Widok tablicy">
              <ViewKanbanOutlinedIcon sx={{ fontSize: 18, mr: { sm: 0.75 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Tablica</Box>
            </ToggleButton>
            <ToggleButton value="list" aria-label="Widok listy">
              <ViewListOutlinedIcon sx={{ fontSize: 18, mr: { sm: 0.75 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Lista</Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Collapse in={showFilters} id="task-filters">
          <Paper sx={{ p: { xs: 2, md: 2.5 }, mt: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              }}
            >
              <TextField
                select size="small" label="Status" value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {STATUS_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[s].dot, mr: 1.25, display: 'inline-block', flexShrink: 0 }} />
                    {STATUS_LABEL[s]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Priorytet" value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority | '')}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {PRIORITY_ORDER.map((p) => (
                  <MenuItem key={p} value={p}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLOR[p], mr: 1.25, display: 'inline-block', flexShrink: 0 }} />
                    {PRIORITY_LABEL[p]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Pracownik" value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Wszyscy</MenuItem>
                {members.map((m) => (
                  <MenuItem key={m.userId} value={m.userId}>{userLabel({ id: m.userId, ...m })}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Kategoria" value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Box>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                label="Tylko z trwającym pomiarem czasu"
                icon={withActiveTimer ? <CheckIcon /> : undefined}
                onClick={() => setWithActiveTimer((v) => !v)}
                color={withActiveTimer ? 'primary' : 'default'}
                variant={withActiveTimer ? 'filled' : 'outlined'}
                aria-pressed={withActiveTimer}
              />
              <Box sx={{ flexGrow: 1 }} />
              {activeFilterCount > 0 && (
                <Button size="small" variant="text" onClick={clearFilters}>Wyczyść filtry</Button>
              )}
            </Stack>
          </Paper>
        </Collapse>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Nie udało się wczytać zadań</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Operacje zbiorcze */}
      {selected.length > 0 && (
        <Paper
          sx={{
            px: 2, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            bgcolor: tokens.plumSofter, borderColor: tokens.plumSoft,
          }}
        >
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, mr: 'auto' }}>
            Zaznaczono {countLabel(selected.length, 'zadanie', 'zadania', 'zadań')}
          </Typography>
          <Button size="small" variant="text" onClick={() => setSelected([])}>Odznacz</Button>
          <Button size="small" variant="outlined" onClick={(e) => setBulkMenu(e.currentTarget)} aria-haspopup="menu">
            Zmień zaznaczone
          </Button>
          <Menu anchorEl={bulkMenu} open={!!bulkMenu} onClose={() => setBulkMenu(null)}>
            <ListSubheader disableSticky sx={menuHeaderSx}>Zmień status</ListSubheader>
            {STATUS_ORDER.filter((s) => s !== 'NEEDS_REWORK').map((s) => (
              <MenuItem key={s} onClick={() => runBulk({ action: 'status', status: s, stopTimer: true })}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[s].dot, mr: 1.25, flexShrink: 0 }} />
                <ListItemText>{STATUS_LABEL[s]}</ListItemText>
              </MenuItem>
            ))}
            <Divider />
            <ListSubheader disableSticky sx={menuHeaderSx}>Zmień priorytet</ListSubheader>
            {PRIORITY_ORDER.map((p) => (
              <MenuItem key={p} onClick={() => runBulk({ action: 'priority', priority: p })}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLOR[p], mr: 1.25, flexShrink: 0 }} />
                <ListItemText>{PRIORITY_LABEL[p]}</ListItemText>
              </MenuItem>
            ))}
            {isManager && [
              <Divider key="d" />,
              <MenuItem key="arch" onClick={() => runBulk({ action: 'archive' })}>
                <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Archiwizuj</ListItemText>
              </MenuItem>,
              <MenuItem key="unarch" onClick={() => runBulk({ action: 'unarchive' })}>
                <ListItemIcon><UnarchiveIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Przywróć z archiwum</ListItemText>
              </MenuItem>,
            ]}
          </Menu>
        </Paper>
      )}

      {/* Gdy wczytanie sie nie udalo, pusty stan z „Nowe zadanie” bylby mylacy — zostaje sam komunikat. */}
      {loadFailed ? null : view === 'board' ? (
        <TaskBoard
          columns={columns}
          loading={loading}
          canDrag
          onOpenTask={(id) => navigate(`/tasks/${id}`)}
          onMoveTask={handleMove}
          onCardMenu={(e, task) => setRowMenu({ el: e.currentTarget, task })}
          emptyState={emptyState}
        />
      ) : (
        <TaskListView
          tasks={rows}
          pagination={pagination}
          loading={loading}
          selected={selected}
          members={members}
          sortBy={sortBy}
          sortDir={sortDir}
          canManage={isManager}
          onSort={(field) => {
            if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
            else { setSortBy(field); setSortDir('asc'); }
          }}
          onSelect={setSelected}
          onOpenTask={(id) => navigate(`/tasks/${id}`)}
          onQuickStatus={handleMove}
          onQuickAssign={handleQuickAssign}
          onCardMenu={(e, task) => setRowMenu({ el: e.currentTarget, task })}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          emptyState={emptyState}
        />
      )}

      {/* Menu zadania */}
      <Menu
        anchorEl={rowMenu?.el}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { navigate(`/tasks/${rowMenu!.task.id}`); setRowMenu(null); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Otwórz szczegóły</ListItemText>
        </MenuItem>
        {isManager && (
          <MenuItem onClick={() => { openEdit(rowMenu!.task); setRowMenu(null); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edytuj</ListItemText>
          </MenuItem>
        )}
        {isManager && !rowMenu?.task.isArchived && (
          <MenuItem
            onClick={async () => {
              await taskApi.archive(rowMenu!.task.id).catch(() => null);
              setRowMenu(null); load();
            }}
          >
            <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Archiwizuj</ListItemText>
          </MenuItem>
        )}
        {isManager && rowMenu?.task.isArchived && (
          <MenuItem
            onClick={async () => {
              await taskApi.unarchive(rowMenu!.task.id).catch(() => null);
              setRowMenu(null); load();
            }}
          >
            <ListItemIcon><UnarchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Przywróć z archiwum</ListItemText>
          </MenuItem>
        )}
        {isManager && <Divider />}
        {isManager && (
          <MenuItem onClick={() => { const task = rowMenu!.task; setRowMenu(null); deleteTask(task); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Usuń</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Powod zwrotu do poprawy (wymagany) */}
      <Dialog open={!!reworkDialog} onClose={() => setReworkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Zwrócić zadanie do poprawy?</DialogTitle>
        <DialogContent>
          {reworkDialog && (
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.25, mb: 2, fontWeight: 600 }}>
              „{reworkDialog.task.title}”
            </Box>
          )}
          <TextField
            autoFocus fullWidth multiline minRows={3} label="Co trzeba poprawić?"
            value={reworkDialog?.reason || ''}
            onChange={(e) => setReworkDialog((d) => (d ? { ...d, reason: e.target.value } : d))}
            helperText="Powód jest wymagany (min. 3 znaki). Zapiszemy go jako komentarz i w historii zadania."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setReworkDialog(null)}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={(reworkDialog?.reason.trim().length || 0) < 3}
            onClick={async () => {
              const d = reworkDialog!;
              setReworkDialog(null);
              await applyStatus(d.task, 'NEEDS_REWORK', { reason: d.reason.trim() });
            }}
          >
            Zwróć do poprawy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trwa pomiar czasu */}
      <Dialog open={!!timerDialog} onClose={() => setTimerDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zatrzymać pomiar czasu?</DialogTitle>
        <DialogContent>
          {timerDialog && (
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.25, mb: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>„{timerDialog.task.title}”</Typography>
              <Typography variant="caption" component="div">Nowy status: {STATUS_LABEL[timerDialog.status]}</Typography>
            </Box>
          )}
          <Typography sx={{ color: tokens.inkSoft }}>
            Przy tym zadaniu trwa pomiar czasu. Zatrzymamy go, zapiszemy zmierzony czas i zmienimy status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setTimerDialog(null)}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const d = timerDialog!;
              setTimerDialog(null);
              await applyStatus(d.task, d.status, { stopTimer: true });
              refreshTimer();
            }}
          >
            Zatrzymaj i zmień status
          </Button>
        </DialogActions>
      </Dialog>

      <TaskFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={() => { load(); setToast({ msg: 'Zapisano zadanie.', severity: 'success' }); }}
        salonId={salonId}
        task={editing}
        members={members}
        categories={categories}
        isManager={isManager}
        currentUserId={user?.id || ''}
      />

      <TaskNotificationSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TasksPage;
